// src/lib/countries.ts
// Single source of truth for country data used across the app
// (signup form flags, bid-log flags, profile settings, etc.)

export interface CountryEntry {
  /** ISO 3166-1 alpha-2 code, e.g. "BE" */
  code: string;
  nl: string;
  en: string;
  fr: string;
}

export const COUNTRIES: CountryEntry[] = [
  { code: "BE", nl: "België",               en: "Belgium",          fr: "Belgique" },
  { code: "NL", nl: "Nederland",            en: "Netherlands",      fr: "Pays-Bas" },
  { code: "FR", nl: "Frankrijk",            en: "France",           fr: "France" },
  { code: "DE", nl: "Duitsland",            en: "Germany",          fr: "Allemagne" },
  { code: "ES", nl: "Spanje",               en: "Spain",            fr: "Espagne" },
  { code: "IT", nl: "Italië",               en: "Italy",            fr: "Italie" },
  { code: "PT", nl: "Portugal",             en: "Portugal",         fr: "Portugal" },
  { code: "GB", nl: "Verenigd Koninkrijk",  en: "United Kingdom",   fr: "Royaume-Uni" },
  { code: "IE", nl: "Ierland",              en: "Ireland",          fr: "Irlande" },
  { code: "BR", nl: "Brazilië",             en: "Brazil",           fr: "Brésil" },
  { code: "AR", nl: "Argentinië",           en: "Argentina",        fr: "Argentine" },
  { code: "US", nl: "Verenigde Staten",     en: "United States",    fr: "États-Unis" },
  { code: "MX", nl: "Mexico",               en: "Mexico",           fr: "Mexique" },
  { code: "TR", nl: "Turkije",              en: "Turkey",           fr: "Turquie" },
  { code: "MA", nl: "Marokko",              en: "Morocco",          fr: "Maroc" },
  { code: "DZ", nl: "Algerije",             en: "Algeria",          fr: "Algérie" },
  { code: "TN", nl: "Tunesië",              en: "Tunisia",          fr: "Tunisie" },
  { code: "NG", nl: "Nigeria",              en: "Nigeria",          fr: "Nigéria" },
  { code: "ZA", nl: "Zuid-Afrika",          en: "South Africa",     fr: "Afrique du Sud" },
  { code: "PL", nl: "Polen",                en: "Poland",           fr: "Pologne" },
  { code: "RO", nl: "Roemenië",             en: "Romania",          fr: "Roumanie" },
  { code: "SE", nl: "Zweden",               en: "Sweden",           fr: "Suède" },
  { code: "NO", nl: "Noorwegen",            en: "Norway",           fr: "Norvège" },
  { code: "DK", nl: "Denemarken",           en: "Denmark",          fr: "Danemark" },
  { code: "AT", nl: "Oostenrijk",           en: "Austria",          fr: "Autriche" },
  { code: "CH", nl: "Zwitserland",          en: "Switzerland",      fr: "Suisse" },
  { code: "LU", nl: "Luxemburg",            en: "Luxembourg",       fr: "Luxembourg" },
  { code: "GR", nl: "Griekenland",          en: "Greece",           fr: "Grèce" },
  { code: "HR", nl: "Kroatië",              en: "Croatia",          fr: "Croatie" },
  { code: "RS", nl: "Servië",               en: "Serbia",           fr: "Serbie" },
  { code: "JP", nl: "Japan",                en: "Japan",            fr: "Japon" },
  { code: "KR", nl: "Zuid-Korea",           en: "South Korea",      fr: "Corée du Sud" },
  { code: "CN", nl: "China",                en: "China",            fr: "Chine" },
  { code: "IN", nl: "India",                en: "India",            fr: "Inde" },
  { code: "AU", nl: "Australië",            en: "Australia",        fr: "Australie" },
];

/** Convert an ISO code to a flag emoji, e.g. "BE" → "🇧🇪" */
export function codeToFlag(code: string): string {
  if (!code || code.length !== 2) return "🏳️";
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join("");
}

/**
 * Normalise any country representation to a 2-letter ISO code.
 * Accepts:
 *  - ISO codes          ("BE", "be")
 *  - Dutch names        ("België", "Nederland")
 *  - English names      ("Belgium", "Netherlands")
 *  - French names       ("Belgique", "Pays-Bas")
 *  - Legacy/common aliases ("Holland", "UK", "England", …)
 */
export function normalizeCountryToCode(country?: string | null): string | undefined {
  if (!country) return undefined;

  const v = country.trim();

  // 1. Direct 2-letter code
  if (v.length === 2) return v.toUpperCase();

  const lower = v.toLowerCase();

  // 2. Build lookup from the shared COUNTRIES list (all languages)
  for (const entry of COUNTRIES) {
    if (
      entry.nl.toLowerCase() === lower ||
      entry.en.toLowerCase() === lower ||
      entry.fr.toLowerCase() === lower
    ) {
      return entry.code;
    }
  }

  // 3. Extra aliases not in the table
  const aliases: Record<string, string> = {
    // Dutch
    "verenigde arabische emiraten": "AE",
    "verenigde staten": "US",
    "holland": "NL",
    "engeland": "GB",
    "groot-brittannië": "GB",
    "groot-brittannie": "GB",
    "brazilie": "BR",
    "brazilIe": "BR",
    "argentinie": "AR",
    "italie": "IT",
    "roemenie": "RO",
    "australie": "AU",
    "denemarken": "DK",
    "noorwegen": "NO",
    "griekenland": "GR",
    "kroatie": "HR",
    "servie": "RS",
    "tunesie": "TN",
    "algerije": "DZ",
    "zwitserland": "CH",
    "oostenrijk": "AT",
    "zweden": "SE",
    "polen": "PL",
    "luxemburg": "LU",
    "zuid-afrika": "ZA",
    "zuid-korea": "KR",
    "nigeria": "NG",
    "marokko": "MA",
    "turkije": "TR",
    "ierland": "IE",
    // English
    "uk": "GB",
    "england": "GB",
    "united kingdom": "GB",
    "great britain": "GB",
    "germany": "DE",
    "deutschland": "DE",
    "france": "FR",
    "spain": "ES",
    "italy": "IT",
    "greece": "GR",
    "croatia": "HR",
    "serbia": "RS",
    "austria": "AT",
    "switzerland": "CH",
    "sweden": "SE",
    "norway": "NO",
    "denmark": "DK",
    "poland": "PL",
    "romania": "RO",
    "luxembourg": "LU",
    "south africa": "ZA",
    "south korea": "KR",
    "brazil": "BR",
    "argentina": "AR",
    "morocco": "MA",
    "algeria": "DZ",
    "tunisia": "TN",
    "turkey": "TR",
    "ireland": "IE",
    "australia": "AU",
    "netherlands": "NL",
    "portugal": "PT",
    "japan": "JP",
    "china": "CN",
    "india": "IN",
    "mexico": "MX",
    // French
    "allemagne": "DE",
    "espagne": "ES",
    "grèce": "GR",
    "grece": "GR",
    "croatie": "HR",
    "serbie": "RS",
    "autriche": "AT",
    "suisse": "CH",
    "suède": "SE",
    "suede": "SE",
    "norvège": "NO",
    "norvege": "NO",
    "danemark": "DK",
    "pologne": "PL",
    "roumanie": "RO",
    "afrique du sud": "ZA",
    "corée du sud": "KR",
    "coree du sud": "KR",
    "brésil": "BR",
    "bresil": "BR",
    "argentine": "AR",
    "maroc": "MA",
    "algérie": "DZ",
    "algerie": "DZ",
    "tunisie": "TN",
    "turquie": "TR",
    "irlande": "IE",
    "pays-bas": "NL",
    "japon": "JP",
    "inde": "IN",
    "mexique": "MX",
    "nigéria": "NG",
    "royaume-uni": "GB",
    "états-unis": "US",
    "etats-unis": "US",
    "belgique": "BE",
    "chine": "CN",
    // "australie" → already covered by NL section above
    // "luxembourg" → already covered by EN section above
    // "portugal" → already covered by EN section above
    // "nigeria" → already covered by EN section above
  };

  return aliases[lower];
}
